import { useState, createContext } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import {
  setLoadingFalseAction,
  setLoadingTrueAction,
} from "../../../redux/actions/loadingActions/loadingActions";
import {
  deliverySuccessMessage,
  modalErrorMessage,
  toastErrorMessage,
} from "../../../utils/toastMessage/toastMessage";
import {
  getProductService,
  getProductsService,
} from "../../../services/publicServices";
import { isEmpty } from "lodash";
import {
  checkDiscountCodeUserService,
  createOrderUserService,
} from "../../../services/userServices";
import { convertRoleToPath } from "../../../utils/enum";
import { setUserAction } from "../../../redux/actions/userActions/userActions";
import { getUsersAdminService } from "../../../services/adminServices";

export const productPublicContext = createContext({
  products: [],
  setProducts: () => {},
  product: {},
  setProduct: () => {},
  discount: {},
  setDiscount: () => {},
  user: {},
  setUser: () => {},
  filter: "",
  setFilter: () => {},
  sort: "",
  setSort: () => {},
  type: "",
  setType: () => {},
  pageCount: null,
  setPageCount: () => {},
  pageNumber: null,
  setPageNumber: () => {},
  itemsCount: null,
  setItemsCount: () => {},
  handleGetProducts: () => {},
  handleGetProduct: () => {},
  handleCreateOrder: () => {},
  handleCheckDiscountCode: () => {},
  handleGetUsers: () => {},
});

const ProductPublicContext = ({ children }) => {
  const dispatch = useDispatch();

  const auth = useSelector((state) => state.auth);

  const { category, product: id } = useParams();

  const [products, setProducts] = useState([]);
  const [product, setProduct] = useState({});
  const [discount, setDiscount] = useState({});
  const [user, setUser] = useState({});
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState("");
  const [type, setType] = useState("");
  const [pageCount, setPageCount] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [itemsCount, setItemsCount] = useState(100);

  // Get Products
  const handleGetProducts = async (event, prd) => {
    event?.preventDefault();

    const isEmptyProduct = isEmpty(prd);

    const objData = isEmptyProduct
      ? {
          ...(category && { category: category }),
          ...(type && { type }),
          filter,
          pageNumber,
          sort,
          itemsCount,
        }
      : { category: prd?.category?._id, pageNumber: 1, itemsCount: 20 };

    try {
      if (isEmptyProduct) dispatch(setLoadingTrueAction());
      const { status, data } = await getProductsService(objData);
      if (status === 200) {
        const result = data.result?.products;
        if (isEmptyProduct) setProducts(result);
        else setProducts(result?.filter((_item) => _item._id !== prd._id));
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Get Product
  const handleGetProduct = async () => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getProductService(id);
      if (status === 200) {
        const result = data.result?.product;
        setProduct(result);
        handleGetProducts(null, result);
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
      dispatch(setLoadingFalseAction());
    }
  };

  // Create Order
  const handleCreateOrder = async (details, gateway) => {
    const objData = {
      details: details,
      product: product._id,
      gateway,
      ...(!isEmpty(user) && { user: user?.value }),
      ...(!isEmpty(discount) && { discountCode: discount?.code }),
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await createOrderUserService(objData);
      if (status === 200) {
        const result = data?.result;
        if (!isEmpty(result?.payment)) {
          deliverySuccessMessage(data.resultMessage);
          window.location.href = result?.payment?.redirectUrl;
        } else {
          dispatch(setUserAction());
          deliverySuccessMessage(
            data.resultMessage,
            `/${convertRoleToPath(auth.role)}/orders`,
            "5000"
          );
        }
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          modalErrorMessage(response.data.resultMessage, false, "5000");
        }
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Check Discount Code
  const handleCheckDiscountCode = async (event) => {
    event?.preventDefault();

    const code = event.target.code.value;

    if (!code) return toastErrorMessage("کد تخفیف الزامی است");

    const objData = {
      product: product._id,
      code,
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await checkDiscountCodeUserService(objData);
      if (status === 200) {
        setDiscount({ ...data.result, ...objData });
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Get Users
  const handleGetUsers = async (filter, callback) => {
    const objData = {
      filter,
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getUsersAdminService(objData);
      if (status === 200) {
        !!callback &&
          callback(
            data.result?.users?.map((item) => ({
              value: item._id,
              label: `${item.firstName} ${item.lastName} - ${item.phoneNumber}`,
            }))
          );
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  return (
    <productPublicContext.Provider
      value={{
        products,
        setProducts,
        product,
        setProduct,
        discount,
        setDiscount,
        user,
        setUser,
        filter,
        setFilter,
        sort,
        setSort,
        type,
        setType,
        pageCount,
        setPageCount,
        pageNumber,
        setPageNumber,
        itemsCount,
        setItemsCount,
        handleGetProducts,
        handleGetProduct,
        handleCreateOrder,
        handleCheckDiscountCode,
        handleGetUsers,
      }}
    >
      {children}
    </productPublicContext.Provider>
  );
};

export default ProductPublicContext;
